import { ADOPT_DATA, AutoUser } from './auto-user';
import { Comment } from './comment';
import { CoordinatorService } from './coordinator.service';
import { ScriptComment } from './script-comment';
import { ScriptHitset } from './script-hitset';
import { ScriptNode } from './script-node';
import { ScriptOutput } from './script-output';
import { ScriptRatings } from './script-ratings';
import { ScriptTags } from './script-tags';
import { ScriptHitsetInterface } from './script-types';
import { ScriptUser } from './script-user';

export type INIT_AUTOUSER = {
    name?: string;
    toProject?: boolean;
    omitComments?: boolean;
    omitRatings?: boolean;
    omitTags?: boolean;
    omit?: string[];
    omitHitset?: ScriptHitset;
    onlyHitset?: ScriptHitset;
};

export class ScriptAutoUser {
    #coords: CoordinatorService;
    #autouser: AutoUser;

    constructor(coords: CoordinatorService, autouser: AutoUser) {
        this.#coords = coords;
        this.#autouser = autouser;
    }

    get id() {
        return this.#autouser.id;
    }

    get name() {
        return this.#autouser.name;
    }

    load(
        {
            commentLs,
            btMap,
        }: {
            commentLs?: Comment[];
            btMap?: Map<string, Map<string, string[]>>;
        },
        { omitComments, omitRatings, omitTags, omit, omitHitset, onlyHitset }: INIT_AUTOUSER,
    ) {
        if (!omitComments && commentLs) {
            this.#autouser.adoptData(commentLs);
        }

        if (btMap) {
            const omitBtSet = this._loadOmitBtSet(!!omitRatings, !!omitTags, omit);
            if (omitBtSet.size) btMap = this._processBtMap(btMap, omitBtSet);
        }

        if (btMap) {
            this.#autouser.adoptData(btMap);
        }
    }

    private _processBtMap(btMap: Map<string, Map<string, string[]>>, omitBtSet: Set<string>) {
        const finalBtMap = new Map<string, Map<string, string[]>>();
        btMap.forEach((mp, itmId) => {
            mp.forEach((ndIdLs, btId) => {
                if (omitBtSet.has(btId)) return;
                let itmMp = finalBtMap.get(itmId);
                if (!itmMp) {
                    itmMp = new Map<string, string[]>();
                    finalBtMap.set(itmId, itmMp);
                }
                let ls = itmMp.get(btId);
                if (!ls) {
                    ls = [];
                    itmMp.set(btId, ls);
                }
                ndIdLs.forEach((ndId) => {
                    ls.push(ndId);
                });
            });
        });
        return finalBtMap;
    }

    private _loadOmitBtSet(omitRatings: boolean, omitTags: boolean, omitLs: string[] = []) {
        const s = new Set<string>();
        if (omitRatings) {
            this.#coords.binaryTree$.list
                .filter((bt) => bt.type === 'ratings')
                .forEach((bt) => s.add(bt.id));
        }
        if (omitTags) {
            this.#coords.binaryTree$.list
                .filter((bt) => bt.type === 'tags')
                .forEach((bt) => s.add(bt.id));
        }
        omitLs.forEach((t) => {
            const bt = this.#coords.binaryTree$.getByTitle('ratings', t);
            bt && s.add(bt.id);
            if (bt) return;
            const bt2 = this.#coords.binaryTree$.getByTitle('tags', t);
            bt2 && s.add(bt2.id);
        });
        return s;
    }

    autouser(arg1: string | INIT_AUTOUSER | boolean = '', arg2: INIT_AUTOUSER | boolean = false) {
        let toProject: boolean;

        if (typeof arg1 !== 'string') {
            arg2 = arg1;
            arg1 = '';
        }

        if (typeof arg2 === 'boolean') {
            toProject = arg2;
            arg2 = {};
        } else {
            if (arg2.name) arg1 = arg2.name;
            toProject = !!arg2.toProject;
        }

        arg1 = arg1?.trim();
        const au = new AutoUser(this.#coords, { name: arg1 || `${this.name}` }).scriptClass;
        au.load({ commentLs: this.#autouser!.commentLs, btMap: this.#autouser!.btMap }, arg2);
        toProject && au.toProject();
        return au;
    }

    has(tag: string) {
        tag = this.#coords.utils$.normalizeString(tag);
        switch (tag) {
            case 'comments':
                return this.#autouser.hasComments;
            default:
                for (const bt of this.#autouser.activeBtLs) {
                    console.log('QWE', this.#coords.utils$.normalizeString(bt.title), tag);
                    if (this.#coords.utils$.normalizeString(bt.title) === tag) return true;
                }
        }
        return false;
    }

    transferDialog(isBulk = false) {
        this.#coords.display$.dialog$.next({
            action: 'autouser-transfer',
            user: this,
            isBulk,
        });
    }

    rename(nm: string) {
        this.#autouser.name = nm;
        return this;
    }

    export() {
        const u = new ScriptOutput(this.#coords);
        u.addAutousers(this);
        u.save();
        return this;
    }

    json() {
        return this.#autouser.json();
    }

    delete() {
        this.#autouser.delete();
    }

    clear(arg: ScriptRatings | ScriptTags | string | 'ratings' | 'tags' | 'comments' | 'all') {
        if (!arg) return console.log('No argument provided for clear()');
        this.#autouser.clear(typeof arg === 'string' ? arg : arg.id);
        return this;
    }

    adopt(
        xu: ScriptAutoUser | ScriptUser,
        {
            mode,
            ratingsMode,
            tagsMode,
            omitComments,
            omitRatings,
            omitTags,
            hitset,
            isBulk,
        }: {
            isBulk?: boolean;
            mode?: 'copy' | 'move';
            ratingsMode?: 'replace' | 'if-empty';
            tagsMode?: 'replace' | 'if-empty' | 'merge';
            omitComments?: boolean;
            omitRatings?: string | string[] | ScriptRatings | ScriptRatings[];
            omitTags?: string | string[] | ScriptTags | ScriptTags[];
            hitset?: ScriptHitset | ScriptHitsetInterface;
        } = {},
    ) {
        const importAU = xu instanceof ScriptUser ? xu.autouser() : xu;

        !omitComments && this.#autouser.adoptData(importAU.provideData('comment'));

        const omitBtSet = new Set<string>();
        this.#coords.binaryTree$.adoptOmitBtIdLs(omitBtSet, 'ratings', omitRatings);
        this.#coords.binaryTree$.adoptOmitBtIdLs(omitBtSet, 'tags', omitTags);

        const inputData = importAU.provideData('binary-tree');

        const params: any = {};
        ratingsMode && (params.ratingsMode = ratingsMode);
        tagsMode && (params.tagsMode = tagsMode);

        !hitset && isBulk && (hitset = this.#coords.bulk$.hitset);

        hitset &&
            (params.itmIdSet = (hitset instanceof ScriptHitset ? hitset : hitset.hitset()).idSet);
        omitBtSet.size && (params.omitBtSet = omitBtSet);

        this.#autouser.adoptData(inputData, params);

        if (mode === 'move') {
            if (!omitComments) xu.clear('comments');
            if (!omitRatings) xu.clear('ratings');
            if (!omitTags) xu.clear('tags');
            if (omitBtSet.size) {
                this.#coords.binaryTree$.list.forEach((bt) => {
                    if (!omitBtSet.has(bt.id)) {
                        this.clear(bt.id);
                    }
                });
            }
        }

        return this;
    }

    provideData(tag: 'comment' | 'binary-tree') {
        return this.#autouser.provideData(tag);
    }

    comment(txt: string, hitset?: ScriptHitset | ScriptHitsetInterface) {
        if (!hitset) this.#autouser.comment(txt);
        else {
            if (!(hitset instanceof ScriptHitset)) hitset = hitset.hitset();
            this.#autouser.comment(txt, (hitset as ScriptHitset).idSet);
        }

        return this;
    }

    get comments() {
        return this.#autouser; //.comments.map((c) => c.scriptClass);
    }

    select(ndLs: ScriptNode | ScriptNode[], hitset: ScriptHitset | ScriptHitsetInterface) {
        if (!Array.isArray(ndLs)) ndLs = [ndLs];
        if (!(hitset instanceof ScriptHitset)) hitset = hitset.hitset();
        const idLs = Array.from((hitset as ScriptHitset).idSet);
        this.#autouser.select(
            ndLs[0].container.id,
            ndLs.map((x) => x.id),
            idLs,
        );
        return this;
    }

    unselect(ndLs: ScriptNode | ScriptNode[], hitset: ScriptHitset | ScriptHitsetInterface) {
        if (!Array.isArray(ndLs)) ndLs = [ndLs];
        if (!(hitset instanceof ScriptHitset)) hitset = hitset.hitset();
        const idLs = Array.from((hitset as ScriptHitset).idSet);
        this.#autouser.unselect(
            ndLs[0].container.id,
            ndLs.map((x) => x.id),
            idLs,
        );
        return this;
    }

    log(prefix = 'ScriptAutoUser') {
        console.log(`${prefix}:`, this.name);
    }

    toProject() {
        this.#autouser.toProject();
        return this;
    }

    inProject() {
        return this.#autouser.inProject;
    }
}
